/**
 * QuestionsPanel — view / edit / reset the assessment questions per segment.
 *
 * View-only by default; an explicit "Edit" toggle unlocks the fields. Each
 * question exposes its three per-segment wordings (Solopreneur / SME / Corporate),
 * the helper line, and the example chips (one per line). Edits take effect on the
 * next assessment, no restart. "Reset" clears the override back to the code default.
 * Edit-only: ids, categories and ordering are fixed in code.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, Pencil, Save, RotateCcw, X, ShieldCheck } from "lucide-react";

const MODE_LABELS = {
  solopreneur: "Solopreneur",
  sme: "SME",
  corporate: "Corporate dept.",
};

export default function QuestionsPanel() {
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/questions");
      setQuestions(data.questions);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  if (loading || !questions) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  return (
    <div className="space-y-6" data-testid="questions-panel">
      <div className="rounded-lg border border-teal-200 dark:border-teal-500/30 bg-teal-50 dark:bg-teal-500/10 p-4 flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-teal-700 dark:text-teal-400 mt-0.5 shrink-0" />
        <div className="text-sm text-teal-900 dark:text-teal-100">
          <p className="font-semibold">These are the questions every respondent answers</p>
          <p className="mt-1 text-teal-900/80 dark:text-teal-100/80">
            Each question has a tailored wording per segment. View by default — toggle <strong>Edit</strong>{" "}
            to change one. Edits apply to the next assessment (no restart). <strong>Reset</strong> restores
            the built-in default. You can change wording, helper text and example chips — not add or remove questions.
          </p>
        </div>
      </div>

      {questions.map((q, i) => (
        <QuestionCard key={q.id} q={q} index={i + 1} onChanged={reload} />
      ))}
    </div>
  );
}

function blankDraft(q) {
  return {
    prompt: { ...q.effective.prompt },
    helper: q.effective.helper || "",
    examples: (q.effective.examples || []).join("\n"),
  };
}

function QuestionCard({ q, index, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(blankDraft(q));
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => { setDraft(blankDraft(q)); }, [q]);

  const save = async () => {
    const { solopreneur, sme, corporate } = draft.prompt;
    if (!solopreneur.trim() || !sme.trim() || !corporate.trim()) {
      toast.error("All three segment wordings are required.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/questions/${q.id}`, {
        prompt: {
          solopreneur: solopreneur.trim(),
          sme: sme.trim(),
          corporate: corporate.trim(),
        },
        helper: draft.helper.trim(),
        examples: draft.examples.split("\n").map((e) => e.trim()).filter(Boolean),
      });
      toast.success("Question updated.");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset this question to the built-in default? Your override will be removed.")) return;
    setResetting(true);
    try {
      await api.delete(`/admin/questions/${q.id}`);
      toast.success("Question reset to default.");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const inputCls = (on) =>
    `mt-1 w-full px-3 py-2 rounded-md border text-sm leading-relaxed focus:outline-none transition-colors ${
      on
        ? "border-teal-500 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50 focus:ring-2 focus:ring-teal-500/20"
        : "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 text-slate-600 dark:text-slate-300 cursor-default"
    }`;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid={`question-card-${q.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-base font-semibold text-slate-900 dark:text-slate-50">
              {index}. <span className="font-mono text-xs text-slate-500 dark:text-slate-400">{q.id}</span>
            </h3>
            {q.has_override ? (
              <span className="px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-500/15 text-teal-800 dark:text-teal-300 text-[10px] font-medium uppercase tracking-wide">Customised</span>
            ) : (
              <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-medium uppercase tracking-wide">Default</span>
            )}
            <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-medium">{q.category}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              data-testid={`question-edit-${q.id}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          ) : (
            <>
              <button
                onClick={() => { setEditing(false); setDraft(blankDraft(q)); }}
                data-testid={`question-cancel-${q.id}`}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid={`question-save-${q.id}`}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
            </>
          )}
          {q.has_override && (
            <button
              onClick={reset}
              disabled={resetting}
              data-testid={`question-reset-${q.id}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-amber-300 dark:border-amber-500/40 text-amber-700 dark:text-amber-300 text-sm font-medium hover:bg-amber-50 dark:hover:bg-amber-500/10 disabled:opacity-50 transition-colors"
            >
              {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Reset
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-3">
        {["solopreneur", "sme", "corporate"].map((m) => (
          <label key={m} className="block text-xs font-medium text-slate-500 dark:text-slate-400">
            {MODE_LABELS[m]}
            <textarea
              value={draft.prompt[m]}
              onChange={(e) => setDraft((d) => ({ ...d, prompt: { ...d.prompt, [m]: e.target.value } }))}
              readOnly={!editing}
              data-testid={`question-prompt-${q.id}-${m}`}
              className={`${inputCls(editing)} h-24`}
            />
          </label>
        ))}
      </div>

      <label className="mt-4 block text-xs font-medium text-slate-500 dark:text-slate-400">
        Helper text
        <input
          value={draft.helper}
          onChange={(e) => setDraft((d) => ({ ...d, helper: e.target.value }))}
          readOnly={!editing}
          data-testid={`question-helper-${q.id}`}
          className={inputCls(editing)}
        />
      </label>

      <label className="mt-4 block text-xs font-medium text-slate-500 dark:text-slate-400">
        Example chips <span className="font-normal text-slate-400">(one per line)</span>
        <textarea
          value={draft.examples}
          onChange={(e) => setDraft((d) => ({ ...d, examples: e.target.value }))}
          readOnly={!editing}
          data-testid={`question-examples-${q.id}`}
          className={`${inputCls(editing)} h-28 font-mono text-[13px]`}
        />
      </label>
    </div>
  );
}
